import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  adminMe,
  adminListInsights,
  adminCreateInsight,
  adminUpdateInsight,
  adminDeleteInsight,
  clearToken,
  getToken,
} from "../lib/api";
import { Plus, Trash2, Pencil, LogOut, ExternalLink } from "lucide-react";
import JourneysAdmin from "./JourneysAdmin";

const EMPTY = { title: "", category: "Reflection", date: "", excerpt: "", bodyText: "", published: true };

export default function Admin() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [view, setView] = useState("insights");
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null); // slug being edited, or "new", or null
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    const data = await adminListInsights();
    setArticles(data);
  };

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    adminMe()
      .then(async () => {
        await load();
        setReady(true);
      })
      .catch(() => {
        clearToken();
        navigate("/admin/login");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startNew = () => {
    setForm(EMPTY);
    setEditing("new");
    setError("");
  };

  const startEdit = (a) => {
    setForm({
      title: a.title,
      category: a.category,
      date: a.date || "",
      excerpt: a.excerpt || "",
      bodyText: (a.body || []).join("\n\n"),
      published: a.published !== false,
    });
    setEditing(a.slug);
    setError("");
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    const payload = {
      title: form.title,
      category: form.category,
      date: form.date,
      excerpt: form.excerpt,
      body: form.bodyText.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean),
      published: form.published,
    };
    try {
      if (editing === "new") await adminCreateInsight(payload);
      else await adminUpdateInsight(editing, payload);
      await load();
      setEditing(null);
    } catch (err) {
      setError(err.response?.data?.detail?.toString() || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (slug) => {
    if (!window.confirm("Delete this insight? This cannot be undone.")) return;
    await adminDeleteInsight(slug);
    await load();
  };

  const logout = () => {
    clearToken();
    navigate("/admin/login");
  };

  if (!ready)
    return <div className="min-h-screen bg-ivory flex items-center justify-center text-ink/40 font-light">Loading…</div>;

  return (
    <div data-testid="admin-page" className="min-h-screen bg-ivory">
      <header className="border-b border-stone">
        <div className="max-w-[1100px] mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-navy.png" alt="Yadid Advisory" className="h-9 w-auto" />
            <span className="eyebrow text-brass border-l border-stone pl-3">Insights Editor</span>
          </div>
          <div className="flex items-center gap-6">
            <div className="hidden sm:flex items-center gap-2 mr-2 border-r border-stone pr-6">
              <button
                data-testid="admin-tab-insights"
                onClick={() => {
                  setView("insights");
                  setEditing(null);
                }}
                className={
                  "text-sm transition-colors " +
                  (view === "insights" ? "text-ink font-semibold" : "text-ink/50 hover:text-ink")
                }
              >
                Insights
              </button>
              <button
                data-testid="admin-tab-journeys"
                onClick={() => {
                  setView("journeys");
                  setEditing(null);
                }}
                className={
                  "text-sm transition-colors " +
                  (view === "journeys" ? "text-ink font-semibold" : "text-ink/50 hover:text-ink")
                }
              >
                Journeys
              </button>
            </div>
            <Link to="/insights" target="_blank" className="text-sm text-ink/60 hover:text-ink inline-flex items-center gap-2">
              View site <ExternalLink size={14} />
            </Link>
            <button data-testid="admin-logout" onClick={logout} className="text-sm text-ink/60 hover:text-ink inline-flex items-center gap-2">
              Sign out <LogOut size={14} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-6 py-12">
        {view === "journeys" ? (
          <JourneysAdmin />
        ) : !editing ? (
          <>
            <div className="flex items-center justify-between mb-10">
              <h1 className="font-serif text-4xl text-ink">Insights</h1>
              <button
                data-testid="admin-new-insight"
                onClick={startNew}
                className="inline-flex items-center gap-2 bg-ink text-ivory px-6 py-3 text-[0.72rem] tracking-[0.16em] uppercase font-semibold hover:bg-[#16294c] transition-colors"
              >
                <Plus size={16} /> New Insight
              </button>
            </div>
            <div className="border-t border-stone">
              {articles.length === 0 && <p className="py-10 text-ink/50 font-light">No insights yet.</p>}
              {articles.map((a) => (
                <div
                  key={a.slug}
                  data-testid={`admin-row-${a.slug}`}
                  className="py-6 border-b border-stone flex items-start justify-between gap-6"
                >
                  <div>
                    <div className="flex items-center gap-3">
                      <p className="eyebrow text-brass">{a.category}</p>
                      {a.published === false && (
                        <span className="text-[0.65rem] uppercase tracking-wider bg-stone text-ink/70 px-2 py-0.5">Draft</span>
                      )}
                    </div>
                    <h3 className="font-serif text-2xl text-ink mt-2">{a.title}</h3>
                    <p className="text-sm text-ink/50 mt-1">{a.date}</p>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <button
                      data-testid={`admin-edit-${a.slug}`}
                      onClick={() => startEdit(a)}
                      className="text-ink/60 hover:text-ink inline-flex items-center gap-1.5 text-sm"
                    >
                      <Pencil size={15} /> Edit
                    </button>
                    <button
                      data-testid={`admin-delete-${a.slug}`}
                      onClick={() => remove(a.slug)}
                      className="text-ink/40 hover:text-red-700 inline-flex items-center gap-1.5 text-sm"
                    >
                      <Trash2 size={15} /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <form onSubmit={save} data-testid="admin-editor-form" className="max-w-3xl">
            <div className="flex items-center justify-between mb-10">
              <h1 className="font-serif text-4xl text-ink">
                {editing === "new" ? "New Insight" : "Edit Insight"}
              </h1>
              <button type="button" onClick={() => setEditing(null)} className="text-sm text-ink/60 hover:text-ink">
                Cancel
              </button>
            </div>
            <div className="space-y-8">
              <div>
                <label className="eyebrow text-ink/50 block mb-3">Title</label>
                <input
                  data-testid="editor-title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  required
                  className="w-full bg-transparent border-0 border-b border-stone py-3 text-2xl font-serif text-ink focus:outline-none focus:border-ink"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="eyebrow text-ink/50 block mb-3">Category</label>
                  <input
                    data-testid="editor-category"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full bg-transparent border-0 border-b border-stone py-3 text-lg font-light text-ink focus:outline-none focus:border-ink"
                  />
                </div>
                <div>
                  <label className="eyebrow text-ink/50 block mb-3">Date label (e.g. June 2026)</label>
                  <input
                    data-testid="editor-date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="w-full bg-transparent border-0 border-b border-stone py-3 text-lg font-light text-ink focus:outline-none focus:border-ink"
                  />
                </div>
              </div>
              <div>
                <label className="eyebrow text-ink/50 block mb-3">Excerpt</label>
                <textarea
                  data-testid="editor-excerpt"
                  value={form.excerpt}
                  onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                  rows={2}
                  className="w-full bg-transparent border-0 border-b border-stone py-3 text-lg font-light text-ink focus:outline-none focus:border-ink resize-none"
                />
              </div>
              <div>
                <label className="eyebrow text-ink/50 block mb-3">
                  Body — blank line = new paragraph · <span className="text-ink/70">**bold**</span> · <span className="text-ink/70">[link](https://…)</span> · start a line with <span className="text-ink/70">&gt;</span> for a pull-quote
                </label>
                <textarea
                  data-testid="editor-body"
                  value={form.bodyText}
                  onChange={(e) => setForm({ ...form, bodyText: e.target.value })}
                  rows={12}
                  className="w-full bg-stone-soft/40 border border-stone p-5 text-base font-light text-ink leading-relaxed focus:outline-none focus:border-ink"
                />
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="editor-published"
                  checked={form.published}
                  onChange={(e) => setForm({ ...form, published: e.target.checked })}
                  className="w-4 h-4 accent-[#0F1E3D]"
                />
                <span className="text-ink/70 font-light">Published (visible on the public site)</span>
              </label>
              {error && <p className="text-sm text-red-700 font-light">{error}</p>}
              <div className="flex items-center gap-4 pt-4">
                <button
                  type="submit"
                  data-testid="editor-save"
                  disabled={saving}
                  className="bg-ink text-ivory px-10 py-4 text-[0.78rem] tracking-[0.16em] uppercase font-semibold hover:bg-[#16294c] transition-colors disabled:opacity-50"
                >
                  {saving ? "Saving…" : "Save Insight"}
                </button>
                <button type="button" onClick={() => setEditing(null)} className="text-ink/60 hover:text-ink text-sm">
                  Cancel
                </button>
              </div>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
